/**
 * Google Maps link handling shared by agent tools and channel code.
 *
 * Two concerns live here, deliberately kept pure so both the WhatsApp
 * inbound path and the agent tool layer read from one definition:
 *
 * 1. **Detection** — is there a Maps URL in this free text, and where?
 * 2. **Cheap coordinate extraction** — long URLs embed coordinates, so
 *    they can be read without a network hop. Short links cannot; those
 *    need `GoogleMapAPIService.unfurlAndParseGoogleMapsUrl` server-side.
 */

/**
 * Hosts and path shapes Google actually issues. `maps.app.goo.gl` is
 * what the mobile share sheet produces today; `goo.gl/maps` is the
 * older short form; the rest are long/desktop URLs.
 */
const MAPS_URL_PATTERN =
  /https?:\/\/(?:(?:maps\.app\.goo\.gl|goo\.gl)\/(?:maps\/)?[\w-]+|(?:www\.)?google\.[a-z.]{2,6}\/maps\S*|maps\.google\.[a-z.]{2,6}\/\S*|(?:www\.)?google\.[a-z.]{2,6}\/url\?\S*maps\S*)/i;

const SHORT_LINK_PATTERN = /https?:\/\/(?:maps\.app\.goo\.gl|goo\.gl\/maps)\/\S+/i;

/**
 * Pull the first Google Maps URL out of free text.
 *
 * Trailing punctuation is stripped because users paste links mid-sentence
 * ("here it is https://maps.app.goo.gl/abc123, thanks") and WhatsApp does
 * not linkify away the comma.
 */
export const extractGoogleMapsUrl = (text: string | undefined | null): string | undefined => {
  if (!text) return undefined;
  const match = text.match(MAPS_URL_PATTERN);
  if (!match) return undefined;
  return match[0].replace(/[).,;:!'"\]}>]+$/, '');
};

/** `true` when the text contains any Google Maps URL. */
export const containsGoogleMapsUrl = (text: string | undefined | null): boolean =>
  extractGoogleMapsUrl(text) !== undefined;

/** `true` when the URL is a short link whose coordinates require unfurling. */
export const isGoogleMapsShortLink = (url: string | undefined | null): boolean =>
  Boolean(url) && SHORT_LINK_PATTERN.test(url as string);

/**
 * Parse coordinates out of a Google Maps link pasted into a text
 * message. Covers the common shapes drivers actually send:
 * - `google.com/maps/@24.7136,46.6753,15z` (long URL)
 * - `google.com/maps/search/24.8076,+46.594535` (dropped pin — what a
 *   `maps.app.goo.gl` share of a long-press pin redirects to)
 * - `google.com/maps?q=24.7136,46.6753` / `maps.google.com/?q=…`
 * - `google.com/maps/place/…!3d24.7136!4d46.6753…`
 * - bare `24.7136, 46.6753` when the whole message is just a coord pair
 *
 * Short links (`maps.app.goo.gl`, `goo.gl/maps`) carry no coords in the
 * URL itself. Callers that need those resolved must fall back to
 * `GoogleMapAPIService.unfurlAndParseGoogleMapsUrl`, which follows the
 * redirects server-side.
 */
export const parseMapsLinkCoords = (
  text: string,
): { lat: number; lng: number } | null => {
  // `!3d!4d` is the resolved place and beats `@lat,lng`, which is only
  // the map centre — the same priority `parseLongGoogleMapsUrl` uses.
  const bang = text.match(/!3d(-?\d{1,2}\.\d+)!4d(-?\d{1,3}\.\d+)/);
  if (bang) return validCoords(Number(bang[1]), Number(bang[2]));

  // `/maps/search/<lat>,+<lng>` and `/maps/dir/…/<lat>,<lng>`. Google
  // encodes the space after the comma as `+` (or `%20`), and these are a
  // *dropped pin* — the user's actual finger position — so they rank
  // above `@`, which is only where the camera happened to sit.
  const path = text.match(
    /\/maps\/(?:search|dir)\/(?:[^/@?]*\/)?(-?\d{1,2}\.\d+)(?:,|%2C)(?:\+|%20|\s)*(-?\d{1,3}\.\d+)/i,
  );
  if (path) return validCoords(Number(path[1]), Number(path[2]));

  const at = text.match(/@(-?\d{1,2}\.\d+),(-?\d{1,3}\.\d+)/);
  if (at) return validCoords(Number(at[1]), Number(at[2]));

  const q = text.match(
    /[?&](?:q|query|ll|center|destination|daddr|saddr)=(-?\d{1,2}\.\d+)(?:,|%2C)(?:\+|%20|\s)*(-?\d{1,3}\.\d+)/i,
  );
  if (q) return validCoords(Number(q[1]), Number(q[2]));

  const bare = text.trim().match(/^(-?\d{1,2}\.\d{3,}),\s*(-?\d{1,3}\.\d{3,})$/);
  if (bare) return validCoords(Number(bare[1]), Number(bare[2]));

  return null;
};

/**
 * `true` when the text contains a Google Maps short link that carries no
 * coords — i.e. the caller must unfurl server-side before it has a pin.
 */
export const hasUnresolvableMapsShortLink = (text: string): boolean =>
  SHORT_LINK_PATTERN.test(text) && parseMapsLinkCoords(text) === null;

const validCoords = (
  lat: number,
  lng: number,
): { lat: number; lng: number } | null => {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return { lat, lng };
};
